import { LoadUserFn } from '@common/types/load-user-fn';
import { IUser } from '@konova/api-types';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { UserContext } from './user-context';
import { useAsync } from '@konova/ui/use-async';

export type UserProviderProps = PropsWithChildren<{
  /**
   * Initial user
   */
  user?: IUser | null;

  /**
   * Function to load the user data
   */
  loadUser?: LoadUserFn;
}>;

export const UserProvider: FC<UserProviderProps> = ({
  user,
  children,
  loadUser,
}) => {
  const [currentUser, setCurrentUser] = useState<IUser | null>(user ?? null);

  const isLoggedIn = useMemo(() => !!currentUser, [currentUser]);
  const spaceUser = useMemo(() => currentUser?.spaceUser ?? null, [currentUser]);
  const isAdmin = useMemo(
    () => currentUser?.spaceUser?.role === 1 || currentUser?.platform_role === 1,
    [currentUser?.spaceUser]
  );
  const isPlatformAdmin = useMemo(
    () => currentUser?.platform_role === 1,
    [currentUser?.platform_role]
  );

  const isAnonymous = useMemo(() => currentUser?.anonymous ?? false, [currentUser]);

  const loadUserFn = useAsync(loadUser ?? (() => Promise.resolve(null)));

  useEffect(() => {
    setCurrentUser(user ?? null);
  }, [user]);

  return (
    <UserContext.Provider
      value={{
        isLoggedIn,
        spaceUser,
        user: currentUser,
        isAdmin,
        isPlatformAdmin,
        isAnonymous,
        setUser: setCurrentUser,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-expect-error
        loadUser: loadUserFn.run,
      }}
    >
      {children}
    </UserContext.Provider >
  );
};